import type OpenAI from "openai";

import { BASE_URL } from "@config/constants";

/**
 * Applies a series of functions to a value, passing the result of each function as the input to the next function.
 *
 * @param value - The initial value to be passed through the pipeline.
 * @param fns - An array of functions to be applied to the value.
 * @returns The final result after applying all the functions in the pipeline.
 *
 * @template T - The type of the value being passed through the pipeline.
 */
export function pipe<T>(value: T, ...fns: Array<(value: T) => T>) {
  return fns.reduce((acc, fn) => fn(acc), value);
}

/**
 * Finds citations in the given text and replaces them with the specified replacement.
 *
 * @param text - The text to search for citations.
 * @param replacement - The replacement string for each citation. Defaults to '<Citation>$1</Citation>'.
 * @returns The modified text with citations replaced.
 */
export function findCitations(
  text: string,
  replacement: string = "<Citation>$1</Citation>"
): string {
  const CITATION_REGEX = /\(\((\d+(?:\)\(\d+)*)\)\)/g;

  return text.replace(CITATION_REGEX, (_, groupInside: string): string => {
    const numbers = groupInside.match(/\d+/g) || [];

    return numbers.map((num) => replacement.replace("$1", num)).join(" ");
  });
}

/**
 * Inserts citations into each paragraph of a message.
 *
 * @param citations - The citations to be inserted.
 * @param message - The original message.
 * @returns The updated message with citations inserted.
 */
export function insertCitationsByParagraph(
  citations: { paragraph: number; source_index: number }[],
  message: string
): string {
  if (!message) return message;

  const paragraphs = message.split("\n\n");

  const updatedParagraphs = citations.reduce((updatedParagraphs, citation) => {
    const { paragraph, source_index } = citation;

    if (paragraph < updatedParagraphs.length) {
      let text = updatedParagraphs[paragraph].trim();
      const multipleCitationRegex = /\(\(\d+(?:\)\(\d+)*\)\)$/;

      if (multipleCitationRegex.test(text)) {
        text = text.replace(/\)\)$/, `)(${source_index}))`);
      } else {
        text += `((${source_index}))`;
      }

      updatedParagraphs[paragraph] = text;
    }

    return updatedParagraphs;
  }, paragraphs);

  return updatedParagraphs.join("\n\n");
}

export function escapeMarkdown(text: string) {
  return text.replace(/(?<!<[^>]*)([_*#>[\]()`-])/g, "\\$1");
}

export function transformMarkdown({
  text,
  disableCitations,
}: {
  text: string;
  disableCitations?: boolean;
}) {
  const processCitations = disableCitations ? (txt: string) => txt : findCitations;
  return pipe(text, processCitations);
}

export function getFileUrl(
  file_path:
    | OpenAI.Beta.Threads.Messages.FilePathAnnotation.FilePath
    | OpenAI.Beta.Threads.Messages.ImageFile
) {
  const url = new URL(BASE_URL);
  url.pathname = `api/v1/scout/files/${file_path.file_id}/`;
  return url.toString();
}

export function isTextContent(
  content: OpenAI.Beta.Threads.Message["content"][number]
): content is OpenAI.Beta.Threads.Messages.TextContentBlock {
  return content.type === "text";
}

export function isFileContent(
  content: OpenAI.Beta.Threads.Message["content"][number]
): content is OpenAI.Beta.Threads.Messages.ImageFileContentBlock {
  return content.type === "image_file";
}

export function isImageUrlContent(
  content: OpenAI.Beta.Threads.Message["content"][number]
): content is OpenAI.Beta.Threads.Messages.ImageURLContentBlock {
  return content.type === "image_url";
}
